import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { api } from '../../api/client';

interface Trainer {
  id: string;
  email: string;
  name: string;
  certified_at: string;
  certificate_no: string | null;
  capacity: number;
  specialisms: string | null;
  is_active: number;
}

export function AdminTrainers() {
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ certificate_no: '', capacity: 12, is_active: true });

  const fetchTrainers = () => {
    api.get<Trainer[]>('/admin/trainers')
      .then(setTrainers)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(fetchTrainers, []);

  const startEdit = (t: Trainer) => {
    setEditing(t.id);
    setEditForm({
      certificate_no: t.certificate_no || '',
      capacity: t.capacity,
      is_active: !!t.is_active,
    });
  };

  const saveEdit = async (id: string) => {
    await api.patch(`/admin/trainers/${id}`, editForm);
    setEditing(null);
    fetchTrainers();
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto mt-10 px-6 pb-20">
        <h1 className="font-display text-3xl font-bold text-mustard-green-dark mb-2">Trainer Management</h1>
        <p className="text-mustard-charcoal/60 mb-8">{trainers.length} registered trainers</p>

        {loading ? (
          <p className="text-mustard-charcoal/60">Loading...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {trainers.map((t) => (
              <div key={t.id} className={`bg-white rounded-xl p-6 shadow-sm border ${t.is_active ? 'border-gray-100' : 'border-red-200 opacity-60'}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-medium text-mustard-charcoal">{t.name}</h3>
                    <p className="text-xs text-mustard-charcoal/50">{t.email}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded font-medium ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {editing === t.id ? (
                  <div className="space-y-2 mt-3 pt-3 border-t border-gray-100">
                    <input type="text" placeholder="Certificate No" value={editForm.certificate_no} onChange={(e) => setEditForm({ ...editForm, certificate_no: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" />
                    <div className="flex gap-2">
                      <input type="number" min={1} max={50} value={editForm.capacity} onChange={(e) => setEditForm({ ...editForm, capacity: Number(e.target.value) })} className="w-20 px-3 py-2 border rounded text-sm" />
                      <label className="flex items-center gap-1 text-sm">
                        <input type="checkbox" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} />
                        Active
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => saveEdit(t.id)} className="px-3 py-1 bg-mustard-green-dark text-white text-xs rounded">Save</button>
                      <button onClick={() => setEditing(null)} className="px-3 py-1 border text-xs rounded">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
                    <div className="text-xs text-mustard-charcoal/50 space-y-1">
                      <p>Cert: {t.certificate_no || 'Not assigned'}</p>
                      <p>Capacity: {t.capacity} clients</p>
                      <p>Certified: {new Date(t.certified_at).toLocaleDateString('en-GB')}</p>
                    </div>
                    <button onClick={() => startEdit(t)} className="text-xs text-mustard-green font-medium hover:underline">Edit</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
